import { useState, useEffect } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Eye, Calendar, User, Hotel, CreditCard, FileText,
  Loader2, LogOut, LogIn, Edit, Clock, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { useTabs, TAB_TYPES } from '@/context/TabsContext';

export default function BookingDetailsTab({ tabId, data }) {
  const { closeTab, openTab } = useTabs();
  const { bookingId, booking: initialBooking } = data || {};
  
  const [booking, setBooking] = useState(initialBooking || null);
  const [guest, setGuest] = useState(null);
  const [room, setRoom] = useState(null);
  const [additionalGuests, setAdditionalGuests] = useState([]);
  const [loading, setLoading] = useState(!initialBooking);

  useEffect(() => {
    loadData();
  }, [bookingId]);

  const loadData = async () => {
    try {
      const bookingData = initialBooking || (await api.get(`/bookings/${bookingId}`)).data;
      setBooking(bookingData);
      
      const [guestRes, roomRes] = await Promise.all([
        bookingData.guest_id ? api.get(`/guests/${bookingData.guest_id}`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        bookingData.room_id ? api.get(`/rooms/${bookingData.room_id}`).catch(() => ({ data: null })) : Promise.resolve({ data: null })
      ]);
      
      setGuest(guestRes.data);
      setRoom(roomRes.data);
      
      // Load additional guests
      if (bookingData.additional_guest_ids?.length > 0) {
        const additionalGuestPromises = bookingData.additional_guest_ids.map(guestId => 
          api.get(`/guests/${guestId}`).catch(() => ({ data: null }))
        );
        const additionalGuestResults = await Promise.all(additionalGuestPromises);
        setAdditionalGuests(additionalGuestResults.map(res => res.data).filter(Boolean));
      } else {
        setAdditionalGuests([]);
      }
    } catch (error) {
      toast.error('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const openGuestDetails = () => {
    if (guest) {
      openTab({
        type: TAB_TYPES.GUEST_DETAILS,
        id: guest.id,
        title: guest.name || `${guest.first_name} ${guest.last_name}`,
        data: { guest, guestId: guest.id }
      });
    }
  };

  const openCheckout = () => {
    openTab({
      type: TAB_TYPES.CHECKOUT,
      id: booking.id,
      title: `Checkout - Room ${room?.room_number || booking.room_number || 'N/A'}`,
      data: { booking, onComplete: loadData }
    });
  };

  const openCheckin = () => {
    openTab({
      type: TAB_TYPES.CHECKIN,
      id: booking.id,
      title: `Check-in - ${booking.room_type_name || 'Room'}`,
      data: { bookingId: booking.id, onComplete: loadData }
    });
  };

  const openEditBooking = () => {
    openTab({
      type: TAB_TYPES.EDIT_BOOKING,
      id: booking.id,
      title: `Edit Booking #${booking.id?.substring(0, 8).toUpperCase()}`,
      data: { booking, onComplete: loadData }
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'checked_out': return 'bg-gray-100 text-gray-700';
      case 'checked_in': return 'bg-green-100 text-green-700';
      case 'confirmed': return 'bg-blue-100 text-blue-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-600">Loading booking details...</span>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">Booking not found</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Eye className="h-6 w-6 text-indigo-600" />
            Booking #{booking.id?.substring(0, 8).toUpperCase()}
          </h1>
          <p className="text-gray-500 mt-1">
            Created on {new Date(booking.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`${getStatusColor(booking.status)} px-3 py-1`}>
            {booking.status?.replace('_', ' ').toUpperCase()}
          </Badge>
          {booking.status === 'checked_in' && (
            <Button onClick={openCheckout} className="bg-purple-600 hover:bg-purple-700">
              <LogOut className="h-4 w-4 mr-2" />
              Checkout
            </Button>
          )}
          {['confirmed', 'reserved'].includes(booking.status) && (
            <Button onClick={openCheckin} className="bg-emerald-600 hover:bg-emerald-700">
              <LogIn className="h-4 w-4 mr-2" />
              Check-in
            </Button>
          )}
          {['pending', 'confirmed', 'reserved'].includes(booking.status) && (
            <Button variant="outline" onClick={openEditBooking}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 grid grid-cols-3 gap-6 overflow-hidden">
        <ScrollArea className="col-span-2 pr-4">
          <div className="space-y-4 pb-4">
            {/* Stay Details */}
            <Card>
              <CardHeader className="py-3 px-4 bg-indigo-50 border-b">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-indigo-700">
                  <Calendar className="h-4 w-4" />
                  Stay Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Check-in</p>
                    <p className="font-semibold text-gray-800">
                      {new Date(booking.check_in_date || booking.check_in).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Check-out</p>
                    <p className="font-semibold text-gray-800">
                      {new Date(booking.check_out_date || booking.check_out).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Duration</p>
                    <p className="font-semibold text-gray-800">
                      {booking.total_nights || (() => {
                        const checkIn = new Date(booking.check_in_date || booking.check_in);
                        const checkOut = new Date(booking.check_out_date || booking.check_out);
                        const nights = Math.max(1, Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24)));
                        return isNaN(nights) ? 1 : nights;
                      })()} Night(s)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Room Details */}
            <Card>
              <CardHeader className="py-3 px-4 bg-blue-50 border-b">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-blue-700">
                  <Hotel className="h-4 w-4" />
                  Room Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Room Number</p>
                    <p className="font-semibold text-gray-800">
                      {room?.room_number || booking.room_number || (booking.room_id ? 'Loading...' : 'Not Assigned')}
                    </p>
                    {!room && !booking.room_number && !booking.room_id && (
                      <p className="text-xs text-yellow-600 mt-1">Room will be assigned at check-in</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Room Type</p>
                    <p className="font-semibold text-gray-800">{room?.room_type || room?.type_name || booking.room_type_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Rate/Night</p>
                    <p className="font-semibold text-gray-800">₹{booking.room_rate?.toFixed(0) || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Floor</p>
                    <p className="font-semibold text-gray-800">{room?.floor || 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Guest Details */}
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={openGuestDetails}>
              <CardHeader className="py-3 px-4 bg-cyan-50 border-b">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-cyan-700">
                  <User className="h-4 w-4" />
                  Guest Details
                  <span className="text-xs text-cyan-500 ml-auto">Click to view full profile →</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Name</p>
                    <p className="font-semibold text-gray-800">{guest?.name || `${guest?.first_name || ''} ${guest?.last_name || ''}`.trim() || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Phone</p>
                    <p className="font-semibold text-gray-800">{guest?.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Email</p>
                    <p className="font-semibold text-gray-800">{guest?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">ID</p>
                    <p className="font-semibold text-gray-800">{guest?.id_type_name || guest?.id_type || 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Guests */}
            {additionalGuests.length > 0 && (
              <Card>
                <CardHeader className="py-3 px-4 bg-purple-50 border-b">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-purple-700">
                    <Users className="h-4 w-4" />
                    Additional Guests ({additionalGuests.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {additionalGuests.map((ag, index) => (
                      <div key={ag.id || index} className="p-3 bg-purple-50/50 rounded-lg border border-purple-100">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Name</p>
                            <p className="font-semibold text-gray-800">
                              {ag.name || `${ag.first_name || ''} ${ag.last_name || ''}`.trim() || `Guest ${index + 1}`}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Phone</p>
                            <p className="font-semibold text-gray-800">{ag.phone || 'N/A'}</p>
                          </div>
                          {ag.id_type_name && (
                            <div>
                              <p className="text-xs text-gray-500 uppercase">ID Type</p>
                              <p className="font-semibold text-gray-800">{ag.id_type_name}</p>
                            </div>
                          )}
                          {ag.id_number && (
                            <div>
                              <p className="text-xs text-gray-500 uppercase">ID Number</p>
                              <p className="font-semibold text-gray-800">{ag.id_number}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            {booking.notes && (
              <Card>
                <CardHeader className="py-3 px-4 bg-gray-50 border-b">
                  <CardTitle className="text-sm font-medium">Notes</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <p className="text-gray-600">{booking.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>

        {/* Right Column - Payment Summary */}
        <div>
          <Card>
            <CardHeader className="py-3 px-4 bg-gradient-to-r from-green-50 to-blue-50 border-b">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Payment Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Room Rate</span>
                <span className="font-medium">₹{booking.room_rate?.toFixed(0) || 0}/night</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Nights</span>
                <span className="font-medium">
                  {booking.total_nights || (() => {
                    const checkIn = new Date(booking.check_in_date || booking.check_in);
                    const checkOut = new Date(booking.check_out_date || booking.check_out);
                    const nights = Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24));
                    return isNaN(nights) || nights < 1 ? 1 : nights;
                  })()}
                </span>
              </div>
              {booking.discount > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Discount</span>
                  <span>- ₹{booking.discount?.toFixed(0)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Tax</span>
                <span className="font-medium">₹{booking.tax_amount?.toFixed(0) || 0}</span>
              </div>
              <div className="border-t pt-2 flex justify-between text-lg">
                <span className="font-bold">Total</span>
                <span className="font-bold text-indigo-600">₹{booking.total_amount?.toFixed(0) || 0}</span>
              </div>
              <div className="flex justify-between text-green-600">
                <span>Advance Paid</span>
                <span className="font-medium">- ₹{booking.advance_paid?.toFixed(0) || 0}</span>
              </div>
              <div className="border-t pt-2 flex justify-between">
                <span className="font-medium">Balance Due</span>
                <span className={`font-bold ${(booking.total_amount - (booking.advance_paid || 0)) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  ₹{((booking.total_amount || 0) - (booking.advance_paid || 0)).toFixed(0)}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card className="mt-4">
            <CardHeader className="py-3 px-4 bg-gray-50 border-b">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Timeline
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <div className="h-2 w-2 rounded-full bg-gray-400"></div>
                  <span className="text-gray-600">Created</span>
                  <span className="ml-auto font-medium">{new Date(booking.created_at).toLocaleDateString()}</span>
                </div>
                {booking.checked_in_at && (
                  <div className="flex items-center gap-2 text-sm">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    <span className="text-gray-600">Checked In</span>
                    <span className="ml-auto font-medium">{new Date(booking.checked_in_at).toLocaleDateString()}</span>
                  </div>
                )}
                {booking.checked_out_at && (
                  <div className="flex items-center gap-2 text-sm">
                    <div className="h-2 w-2 rounded-full bg-purple-500"></div>
                    <span className="text-gray-600">Checked Out</span>
                    <span className="ml-auto font-medium">{new Date(booking.checked_out_at).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Button
            variant="outline"
            onClick={() => closeTab(tabId)}
            className="w-full mt-4"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
